import {
  initialConversation,
  type ConversationState,
  type Message
} from "./conversation";

/**
 * The conversation lives in `sessionStorage`: it survives closing the drawer,
 * navigating inside the tab and a reload, and dies with the tab. It holds what
 * the salon typed and what the assistant answered, so it is also cleared on
 * logout (`pages/logout`) and is keyed to the customer it belongs to - a
 * different salon signing in on the same tab never sees it.
 */
const STORAGE_KEY = "ss_order_assistant";

const STORAGE_VERSION = 1;

type Stored = {
  v: number;
  customerId: string;
  sessionId: string | null;
  messages: Message[];
  draft: string;
  ended: ConversationState["ended"];
  limit: ConversationState["limit"];
};

/** Forget the stored conversation and session id. */
export function clearStoredConversation(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode, embedded webview): nothing to clear.
  }
}

/** Persist the conversation for `customerId`. Never throws. */
export function saveConversation(
  customerId: string,
  state: ConversationState
): void {
  const stored: Stored = {
    v: STORAGE_VERSION,
    customerId,
    sessionId: state.sessionId,
    messages: state.messages,
    draft: state.draft,
    ended: state.ended,
    limit: state.limit
  };
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Quota or unavailable storage: the conversation just will not survive a
    // reload.
  }
}

/**
 * The stored conversation for `customerId`, or the empty one. A conversation
 * that belongs to another customer is discarded, not returned. A turn that was
 * still running when the page went away cannot be resumed, so it comes back as
 * a failed turn the salon can retry.
 */
export function loadConversation(customerId: string): ConversationState {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return initialConversation;

    const stored = JSON.parse(raw) as Partial<Stored> | null;
    if (
      !stored ||
      stored.v !== STORAGE_VERSION ||
      stored.customerId !== customerId ||
      !Array.isArray(stored.messages)
    ) {
      if (stored && stored.customerId !== customerId) {
        clearStoredConversation();
      }
      return initialConversation;
    }

    return {
      ...initialConversation,
      sessionId: typeof stored.sessionId === "string" ? stored.sessionId : null,
      messages: stored.messages.map((message) =>
        message.role === "assistant" && message.status === "streaming"
          ? {
              ...message,
              status: "failed" as const,
              statusLine: null,
              failure: { code: "network" as const, fields: {} }
            }
          : message
      ),
      draft: typeof stored.draft === "string" ? stored.draft : "",
      ended: stored.ended ?? null,
      limit: stored.limit ?? null
    };
  } catch {
    return initialConversation;
  }
}
